import { useEffect, useRef, useCallback, RefObject } from "react";

const FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface UseFocusTrapOptions {
    isActive: boolean;
    onEscape: () => void;
    initialFocusRef?: RefObject<HTMLElement | null>;
}

export const useFocusTrap = (
    containerRef: RefObject<HTMLElement | null>,
    { isActive, onEscape, initialFocusRef }: UseFocusTrapOptions,
) => {
    const previousFocusRef = useRef<HTMLElement | null>(null);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onEscape();
                return;
            }

            if (event.key === "Tab" && containerRef.current) {
                const focusableElements =
                    containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
                const firstFocusable = focusableElements[0];
                const lastFocusable = focusableElements[focusableElements.length - 1];

                if (event.shiftKey && document.activeElement === firstFocusable) {
                    event.preventDefault();
                    lastFocusable?.focus();
                } else if (!event.shiftKey && document.activeElement === lastFocusable) {
                    event.preventDefault();
                    firstFocusable?.focus();
                }
            }
        },
        [containerRef, onEscape],
    );

    useEffect(() => {
        if (!isActive) return;

        previousFocusRef.current = document.activeElement as HTMLElement;

        const focusTarget = initialFocusRef?.current ?? null;
        focusTarget?.focus();

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            previousFocusRef.current?.focus();
        };
    }, [isActive, handleKeyDown, initialFocusRef]);
};
